import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { groupCarriersByName } from '../../utils/carrierHelpers';

function AgentChangeRequests({ token }) {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [carriers, setCarriers] = useState([]);

  useEffect(() => {
    fetchCarriers();
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const fetchCarriers = async () => {
    try {
      const response = await axios.get('/api/agent/carriers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCarriers(response.data || []);
    } catch (error) {
      console.error('Error fetching carriers:', error);
    }
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/agent/change-requests', {
        headers: { Authorization: `Bearer ${token}`, 'X-Campaign-ID': localStorage.getItem('selectedCampaign') || 'medicare' }
      });
      let reqs = response.data || [];
      
      // Filter by status
      // ⭐ 'approved' filter shows both 'approved' and 'auto_approved'
      if (filter === 'approved') {
        reqs = reqs.filter(req => req.status === 'approved' || req.status === 'auto_approved');
      } else if (filter !== 'all') {
        reqs = reqs.filter(req => req.status === filter);
      }
      
      // Sort by newest first
      reqs = reqs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      setRequests(reqs);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  // ⭐ Get friendly status label and style for display
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'auto_approved':
        return {
          label: '⚡ Auto-Approved',
          style: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '3px 10px',
            background: '#e0f2fe',
            color: '#0369a1',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600',
            border: '1px solid #7dd3fc'
          }
        };
      case 'approved':
        return {
          label: '✅ Approved',
          style: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '3px 10px',
            background: '#d1fae5',
            color: '#065f46',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600',
            border: '1px solid #6ee7b7'
          }
        };
      case 'pending':
        return {
          label: '⏳ Pending Review',
          style: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '3px 10px',
            background: '#fef3c7',
            color: '#92400e',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600',
            border: '1px solid #fcd34d'
          }
        };
      case 'rejected':
        return {
          label: '❌ Rejected',
          style: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '3px 10px',
            background: '#fee2e2',
            color: '#991b1b',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600',
            border: '1px solid #fca5a5'
          }
        };
      default:
        return {
          label: status,
          style: {}
        };
    }
  };

  const renderChanges = (request) => {
    if (!request || !request.changes) return null;

    return (
      <div className="request-changes" style={{ padding: '8px 0' }}>
        {request.changes.statesWithCarriers && (
          <p>✓ States & Carriers updated</p>
        )}
        {request.changes.consent && (
          <p>✓ Consent updated</p>
        )}
        {request.changes.healthSherpaMarketingLink !== undefined && (
          <p>✓ HealthSherpa Marketing Link updated</p>
        )}
      </div>
    );
  };

  if (loading) return <div className="loading">Loading your change requests...</div>;

  return (
    <div className="container">
      <div className="section-header">
        <h2>My Change Requests</h2>
        <div className="filter-buttons">
          <button 
            className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button 
            className={`btn btn-sm ${filter === 'pending' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter('pending')}
          >
            Pending
          </button>
          <button 
            className={`btn btn-sm ${filter === 'approved' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter('approved')}
          >
            Approved
          </button>
          <button 
            className={`btn btn-sm ${filter === 'rejected' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter('rejected')}
          >
            Rejected
          </button>
        </div>
      </div>

      <div className="request-list">
        {requests.length === 0 ? (
          <div className="empty-state">
            {filter === 'all' ? 'No change requests yet' : `No ${filter} change requests`}
          </div>
        ) : (
          requests.map(request => {
            const statusDisplay = getStatusDisplay(request.status);
            return (
              <div key={request.id} className="request-card" style={{
                // ⭐ Add left border color based on status
                borderLeft: request.status === 'auto_approved'
                  ? '4px solid #0ea5e9'
                  : request.status === 'approved'
                  ? '4px solid #10b981'
                  : request.status === 'pending'
                  ? '4px solid #f59e0b'
                  : request.status === 'rejected'
                  ? '4px solid #ef4444'
                  : undefined
              }}>
                <div className="request-header">
                  <div>
                    <h4>Change Request</h4>
                    {request.agentEmail && (
                      <p style={{ fontSize: '14px', color: '#4f46e5', margin: '4px 0 8px 0' }}>
                        Agent: {request.agentEmail}
                      </p>
                    )}
                    <span style={statusDisplay.style}>{statusDisplay.label}</span>
                  </div>
                  <div className="request-date">
                    Submitted: {new Date(request.createdAt).toLocaleString('en-US', {
                      timeZone: 'America/Chicago',
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: true
                    })}
                  </div>
                </div>

                {renderChanges(request)}

                {/* ⭐ Show auto-approval notice */}
                {request.status === 'auto_approved' && (
                  <div style={{
                    marginTop: '12px',
                    padding: '10px 14px',
                    background: '#f0f9ff',
                    borderRadius: '6px',
                    border: '1px solid #bae6fd',
                    fontSize: '13px',
                    color: '#0369a1',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '16px', flexShrink: 0 }}>⚡</span>
                    <div>
                      <strong>Changes applied instantly!</strong>
                      <div style={{ marginTop: '2px', fontSize: '12px' }}>
                        Your request was automatically approved because it only contained additions (no state removals).
                        Your profile has already been updated.
                      </div>
                      {request.autoApprovedAt && (
                        <div style={{ marginTop: '4px', fontSize: '12px', color: '#6b7280' }}>
                          Applied at: {new Date(request.autoApprovedAt).toLocaleString('en-US', {
                            timeZone: 'America/Chicago',
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ⭐ Show pending notice explaining why manual review is needed */}
                {request.status === 'pending' && (
                  <div style={{
                    marginTop: '12px',
                    padding: '10px 14px',
                    background: '#fffbeb',
                    borderRadius: '6px',
                    border: '1px solid #fcd34d',
                    fontSize: '13px',
                    color: '#92400e',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '16px', flexShrink: 0 }}>⏳</span>
                    <div>
                      <strong>Waiting for admin review</strong>
                      <div style={{ marginTop: '2px', fontSize: '12px' }}>
                        Your request contains state removal(s) which require manual admin approval.
                        An admin will review and apply your changes soon.
                      </div>
                    </div>
                  </div>
                )}

                {request.status === 'approved' && (
                  <div style={{
                    marginTop: '12px',
                    padding: '10px 14px',
                    background: '#f0fdf4',
                    borderRadius: '6px',
                    border: '1px solid #86efac',
                    fontSize: '13px',
                    color: '#15803d'
                  }}>
                    ✅ Your changes have been approved and applied to your profile.
                    {request.approvedBy && ` Reviewed by: ${request.approvedBy}`}
                  </div>
                )}

                {request.status === 'rejected' && request.rejectionReason && (
                  <div className="rejection-reason" style={{ marginTop: '12px' }}>
                    <strong>Rejection Reason:</strong> {request.rejectionReason}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default AgentChangeRequests;
